import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";
import { useSyncExternalStore } from "react";
import toast from "react-hot-toast";
import { streamRun, streamReplay, reconnectPlaybookRun, type StreamHandle } from "@/lib/studio/sse";
import { apiPost } from "@/services/studio/client";
import { progressFromEvents } from "@/components/organisms/studio/AgentRunProgress";
import { useActivityStore } from "@/stores/activityStore";
import { getQueryClient } from "@/dataProvider/reactQuery/context";
import { normalizeArtifactValidation } from "@/services/migrations/normalize";
import { runErrorLabel } from "@/lib/studio/utils";
import {
  getActiveOrgId,
  registerOrgScopedState,
  type OrgId,
} from "@/lib/org-scope";
import type { RunArtifact, RunEvent } from "@/services/studio/types";

export type RunStatus = "idle" | "running" | "done" | "error" | "cancelled";

export interface CacheHitInfo {
  age_seconds: number;
  retrieval_id: string;
}

// Per-step LLM streaming buffer. Keyed by step_id. Each step accumulates
// free-form text + a JSON-args buffer for each tool_call index the model
// emits. Lets the UI render the bundle as it forms instead of showing
// only a "(35s)" counter while waiting for the final payload.
export interface StepStreamingState {
  step_id: string;
  tool?: string;
  text: string;                                  // narration tokens so far
  tool_args: Record<number, { tool_name: string; buf: string }>;
}

interface RunState {
  skillId: string | null;
  inputs: Record<string, unknown>;
  events: RunEvent[];
  status: RunStatus;
  result: string;
  errorMessage: string | null;
  run_id: string | null;
  retrieval_id: string | null;
  artifacts: RunArtifact[];
  cache_hit: CacheHitInfo | null;
  replay: boolean;
  activityId: string | null;
  // step_id → live LLM streaming buffer (text + tool_args fragments)
  streaming: Record<string, StepStreamingState>;
  _handle: StreamHandle | null;
  // Set when the user hit Stop before the run_id arrived via 'started'. pushEvent
  // honors it the moment the id lands (see abort for why we can't abort in that window).
  stopRequested: boolean;
  start: (skillId: string, inputs: Record<string, unknown>, skillTitle?: string) => void;
  startReplay: (runId: string) => void;
  // Reconnect to an already-running run's LIVE SSE stream (browser refresh) so the
  // report/steps resume streaming instead of freezing on the one-shot snapshot.
  reconnect: (skillId: string, runId: string, skillTitle?: string) => void;
  applyPersistedRun: (payload: { result?: string; artifacts?: RunArtifact[] }) => void;
  // Seed the store's status/skillId/run_id from a historical (already-terminal)
  // run so the workspace tab label (derived from this store, not from RunTab's
  // local historicalRunId state) shows "Report" consistently — mirrors agents'
  // AgentRunTab writing `phase: "done"` into the shared agentRunStore instead of
  // keeping historical-view state local to the component. No-op while a live run
  // for this skill is in progress so it can't clobber it.
  markHistoricalDone: (skillId: string, runId: string) => void;
  // Discard a shown report and return to the configure form — mirrors agents'
  // Run tab button, which resets phase to "configure" + clears events on every
  // click while phase is already "done" (viewing a report is a one-shot deal;
  // clicking the tab again means "let me run this fresh"). Deliberately narrow
  // (status + events only, matching agents) so run_id/result/artifacts are left
  // for the next start()/reconnect() to overwrite, exactly like agentRunStore.
  resetToConfigure: () => void;
  pushEvent: (e: RunEvent) => void;
  complete: (status: RunStatus, errorMessage?: string) => void;
  reset: () => void;
  abort: () => void;
}

// Tell the backend to actually stop the run. Awaited + error-surfaced (the old
// fire-and-forget `void apiPost` swallowed 404s/500s, so a failed stop looked like
// success while the run kept executing server-side).
function stopPlaybookRunOnServer(runId: string): void {
  void apiPost(`/api/playbook-runs/${encodeURIComponent(runId)}/stop`, {}).catch((err) => {
    console.error("[runStore] failed to stop run on server", err);
    toast.error("Could not stop the run on the server — it may still be running.");
  });
}

const initialState = {
  skillId: null as string | null,
  inputs: {} as Record<string, unknown>,
  events: [] as RunEvent[],
  status: "idle" as RunStatus,
  result: "",
  errorMessage: null as string | null,
  run_id: null as string | null,
  retrieval_id: null as string | null,
  artifacts: [] as RunArtifact[],
  cache_hit: null as CacheHitInfo | null,
  replay: false,
  activityId: null as string | null,
  streaming: {} as Record<string, StepStreamingState>,
  _handle: null as StreamHandle | null,
  stopRequested: false,
};

const MAX_VISIBLE_EVENTS = 240;

// One run-store INSTANCE per skillId. This mirrors agentRunStore's per-agent
// isolation (each agent runs independently) — but as separate store instances
// rather than a single Map, which keeps the ~200-line streaming/event logic below
// byte-for-byte identical to the proven singleton. Two migrations now run fully
// independently: each skillId gets its own status / events / stream handle.
function makeRunStore(): StoreApi<RunState> {
  return createStore<RunState>((set, get) => ({
  ...initialState,

  start: (skillId, inputs, skillTitle) => {
    get()._handle?.abort();

    // Clean up any prior activity
    const prevActivityId = get().activityId;
    if (prevActivityId) useActivityStore.getState().removeActivity(prevActivityId);

    const activityId = `playbook_run_${skillId}_${Date.now()}`;
    const displayName = skillTitle ?? skillId;

    useActivityStore.getState().startActivity(
      activityId,
      'playbook_run',
      `Running ${displayName}…`,
      { skillId, skillTitle: displayName },
    );

    set({
      ...initialState,
      skillId,
      inputs,
      status: "running",
      replay: false,
      activityId,
    });

    const handle = streamRun(skillId, inputs, {
      onEvent: (evt) => {
        get().pushEvent(evt);

        // Bridge to activityStore — same pattern as agentRunStore
        if (evt.event === 'done') {
          useActivityStore.getState().completeActivity(activityId, `${displayName} complete`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
          void getQueryClient().invalidateQueries({ queryKey: ["runs"] });
          void getQueryClient().invalidateQueries({ queryKey: ["run"] });
        } else if (evt.event === 'error') {
          useActivityStore.getState().failActivity(activityId, `${displayName} failed`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
        } else {
          const progress = progressFromEvents(get().events);
          useActivityStore.getState().updateActivity(activityId, {
            progress: progress.percent,
            indeterminate: progress.indeterminate,
            completedSteps: progress.completed,
            message: `Running ${displayName}…`,
          });
        }
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        get().complete("error", msg);
        useActivityStore.getState().failActivity(activityId, `${displayName} failed`);
        setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
      },
      onClose: () => {
        if (get().status !== "running") return;
        // If we have a run_id the run started on the server — stream just dropped
        // (proxy timeout, CDN cut, etc.). Mark done so SkillRunStream loads the
        // persisted result from the DB rather than showing a misleading error state.
        if (get().run_id) {
          get().complete("done");
          void getQueryClient().invalidateQueries({ queryKey: ["runs"] });
          void getQueryClient().invalidateQueries({ queryKey: ["run"] });
          useActivityStore.getState().completeActivity(activityId, `${displayName} complete`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
        } else {
          // Run never started — real failure
          get().complete("error", "stream_closed_before_done");
          useActivityStore.getState().failActivity(activityId, `${displayName} failed`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
        }
      },
    });

    set({ _handle: handle });
  },

  startReplay: (runId) => {
    get()._handle?.abort();
    set({
      ...initialState,
      status: "running",
      replay: true,
      run_id: runId,
    });

    const handle = streamReplay(runId, {
      onEvent: (evt) => {
        get().pushEvent(evt);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        get().complete("error", msg);
      },
      onClose: () => {
        if (get().status === "running") {
          get().complete(get().run_id ? "done" : "error", get().run_id ? undefined : "stream_closed_before_done");
        }
      },
    });

    set({ _handle: handle });
  },

  reconnect: (skillId, runId, skillTitle) => {
    // Already streaming this same run live — don't double-connect.
    if (get()._handle && get().run_id === runId && get().status === "running") return;
    get()._handle?.abort();
    const prevActivityId = get().activityId;
    if (prevActivityId) useActivityStore.getState().removeActivity(prevActivityId);

    const displayName = skillTitle ?? skillId;
    const activityId = `playbook_run_${skillId}_${Date.now()}`;
    useActivityStore.getState().startActivity(
      activityId,
      'playbook_run',
      `Running ${displayName}…`,
      { skillId, skillTitle: displayName },
    );

    // Seed as a live run: known run_id + running status so Stop works and events
    // reduce correctly. No JSON snapshot — the live stream carries the state.
    set({
      ...initialState,
      skillId,
      status: "running",
      replay: false,
      run_id: runId,
      activityId,
    });

    const handle = reconnectPlaybookRun(runId, {
      onEvent: (evt) => {
        get().pushEvent(evt);
        if (evt.event === 'done') {
          useActivityStore.getState().completeActivity(activityId, `${displayName} complete`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
          void getQueryClient().invalidateQueries({ queryKey: ["runs"] });
          void getQueryClient().invalidateQueries({ queryKey: ["run"] });
        } else if (evt.event === 'error') {
          useActivityStore.getState().failActivity(activityId, `${displayName} failed`);
          setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
        } else {
          const progress = progressFromEvents(get().events);
          useActivityStore.getState().updateActivity(activityId, {
            progress: progress.percent,
            indeterminate: progress.indeterminate,
            completedSteps: progress.completed,
            message: `Running ${displayName}…`,
          });
        }
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        get().complete("error", msg);
        useActivityStore.getState().failActivity(activityId, `${displayName} failed`);
        setTimeout(() => useActivityStore.getState().removeActivity(activityId), 5000);
      },
      onClose: () => {
        if (get().status !== "running") return;
        // Reconnect always seeds a known run_id before opening the stream (unlike
        // start(), which may drop before 'started' arrives) — so a dropped stream
        // here (proxy timeout, network blip) means the run already exists on the
        // server. Mark done so SkillRunStream loads the persisted result from the
        // DB rather than showing a misleading failure for a run that's still
        // executing or already finished server-side.
        get().complete("done");
        void getQueryClient().invalidateQueries({ queryKey: ["runs"] });
        useActivityStore.getState().completeActivity(activityId, `${displayName} complete`);
        setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
      },
    });

    set({ _handle: handle });
  },

  applyPersistedRun: (payload) => {
    set((s) => ({
      result: payload.result ?? s.result,
      artifacts: (payload.artifacts?.length ? payload.artifacts : null) ?? s.artifacts,
    }));
  },

  markHistoricalDone: (skillId, runId) => {
    if (get().status === "running") return;
    set({ skillId, run_id: runId, status: "done" });
  },

  resetToConfigure: () => {
    if (get().status === "idle" || get().status === "running") return;
    set({ status: "idle", events: [] });
  },

  pushEvent: (e) => {
    if (e.event !== "render") {
      set((s) => ({
        events: [...s.events, e].slice(-MAX_VISIBLE_EVENTS),
      }));
    }

    if (e.event === "started") {
      const patch: Partial<RunState> = {};
      if (typeof e.run_id === "string") patch.run_id = e.run_id;
      if (typeof e.retrieval_id === "string") {
        patch.retrieval_id = e.retrieval_id;
      }
      if (e.replay === true) patch.replay = true;
      if (Object.keys(patch).length) set(patch);
      // User hit Stop during startup (before this run_id existed). Honor it now that
      // the id has landed — otherwise the run keeps executing with nobody watching.
      if (get().stopRequested && typeof e.run_id === "string") {
        stopPlaybookRunOnServer(e.run_id);
        get()._handle?.abort();
        set({ _handle: null, status: "cancelled", errorMessage: "Stopped by user", stopRequested: false });
      }
    }

    if (e.event === "render") {
      const md =
        typeof e.markdown === "string"
          ? e.markdown
          : typeof e.content === "string"
            ? e.content
            : null;
      if (md) set({ result: md });
    }

    if (e.event === "cache_hit") {
      const age =
        typeof e.age_seconds === "number" ? e.age_seconds : 0;
      const retrieval =
        typeof e.retrieval_id === "string" ? e.retrieval_id : "";
      set({ cache_hit: { age_seconds: age, retrieval_id: retrieval } });
    }

    // ── LLM streaming deltas → accumulate into streaming[step_id] ───────
    if (e.event === "llm_text_delta" || e.event === "llm_tool_args_delta") {
      const sid = typeof e.step_id === "string" ? e.step_id : "";
      if (!sid) return;
      set((s) => {
        const cur = s.streaming[sid] ?? {
          step_id: sid,
          tool: typeof e.tool === "string" ? e.tool : undefined,
          text: "",
          tool_args: {},
        };
        if (e.event === "llm_text_delta") {
          const chunk = typeof e.text === "string" ? e.text : "";
          return {
            streaming: {
              ...s.streaming,
              [sid]: { ...cur, text: cur.text + chunk },
            },
          };
        }
        // llm_tool_args_delta
        const idx = typeof e.tool_index === "number" ? e.tool_index : 0;
        const name = typeof e.tool_name === "string" ? e.tool_name : "";
        const piece = typeof e.args_delta === "string" ? e.args_delta : "";
        const slot = cur.tool_args[idx] ?? { tool_name: name, buf: "" };
        return {
          streaming: {
            ...s.streaming,
            [sid]: {
              ...cur,
              tool_args: {
                ...cur.tool_args,
                [idx]: { tool_name: slot.tool_name || name, buf: slot.buf + piece },
              },
            },
          },
        };
      });
    }

    if (e.event === "step_result") {
      const sid = typeof e.step_id === "string" ? e.step_id : "";
      if (sid) {
        setTimeout(() => {
          set((s) => {
            if (!(sid in s.streaming)) return {};
            const next = { ...s.streaming };
            delete next[sid];
            return { streaming: next };
          });
        }, 2000);
      }
    }

    if (e.event === "artifact") {
      const name = typeof e.name === "string" ? e.name : null;
      if (name) {
        const artifact: RunArtifact = {
          name,
          content_type:
            typeof e.content_type === "string"
              ? e.content_type
              : "application/octet-stream",
          size_bytes:
            typeof e.size_bytes === "number" ? e.size_bytes : 0,
          created_at: new Date().toISOString(),
          // Deployable artifacts always carry a check-only verdict (including
          // "unvalidated"); non-deployable ones carry null and render no badge.
          validation: normalizeArtifactValidation(e.validation),
        };
        set((s) => ({
          artifacts: [
            ...s.artifacts.filter((existing) => existing.name !== artifact.name),
            artifact,
          ],
        }));
      }
    }

    if (e.event === "done") {
      set({ status: "done" });
    }

    if (e.event === "error") {
      get().complete("error", e.message ?? "run_failed");
    }
  },

  complete: (status, errorMessage) => {
    // A second report of the same failure keeps the FIRST message: the `error` frame names the
    // actual cause, the transport error that follows it only says the stream died.
    const alreadyFailed = get().status === "error";
    set({ status, errorMessage: alreadyFailed ? get().errorMessage : errorMessage ?? null });
    // A failed run used to end in silence — the UI simply stopped, and the message sat in the
    // report pane the user had no reason to look at. One toast here covers every surface that
    // drives this store. `alreadyFailed` is the de-dupe: a transport failure arrives twice (an
    // `error` frame AND the stream's onError), and that is one failure, not two.
    if (status === "error" && !alreadyFailed) {
      toast.error(runErrorLabel(errorMessage) ?? "The run failed");
    }
  },

  reset: () => {
    // Clean up any in-progress activity
    const activityId = get().activityId;
    if (activityId) useActivityStore.getState().removeActivity(activityId);
    get()._handle?.abort();
    set({ ...initialState });
  },

  abort: () => {
    const { run_id, activityId } = get();
    // No run_id yet? It only arrives via the 'started' SSE event. Aborting the stream
    // now would ORPHAN the backend run (it keeps executing, no id left to cancel it
    // with — the old code aborted + marked cancelled regardless, so an early Stop
    // silently did nothing server-side). Flag the intent and KEEP the stream alive;
    // pushEvent fires the stop the instant 'started' delivers the id.
    if (!run_id) {
      set({ stopRequested: true });
      return;
    }
    stopPlaybookRunOnServer(run_id);
    get()._handle?.abort();
    if (activityId) {
      const activity = useActivityStore.getState().getActivity(activityId);
      const name = (activity?.metadata?.skillTitle as string | undefined) ?? 'Migration';
      useActivityStore.getState().failActivity(activityId, `${name} stopped`);
      setTimeout(() => useActivityStore.getState().removeActivity(activityId), 3000);
    }
    set({
      _handle: null,
      status: "cancelled",
      errorMessage: "Stopped by user",
      stopRequested: false,
    });
  },
  }));
}

// ── Per-(organization, skill) registry + reactive aggregate ──────────────────
//
// The registry key carries the organization as well as the skill. A playbook run
// executes against one org's metadata, so `getRunStore('apex-cleanup')` in Org B
// must not hand back the store Org A is streaming into — that is what left a
// migration's transcript and its header activity on screen after a switch.
//
// Keying (rather than resetting) means the run keeps streaming into its own
// store while the user is elsewhere, and is intact when they return. Callers are
// unchanged: they still pass a skill id, and the org is resolved here so no call
// site can forget it.
const runStores = new Map<string, StoreApi<RunState>>();
const activeListeners = new Set<() => void>();

function notifyActive() {
  for (const listener of activeListeners) listener();
}

function runStoreKey(orgId: OrgId | null, skillId: string): string {
  return `${orgId ?? '__no_org__'}::${skillId}`;
}

export function getRunStore(skillId: string): StoreApi<RunState> {
  const key = runStoreKey(getActiveOrgId(), skillId);
  let store = runStores.get(key);
  if (!store) {
    store = makeRunStore();
    runStores.set(key, store);
    // A status change in any skill store can flip the "is anything running?"
    // aggregate the migrations list subscribes to (fires outside render).
    store.subscribe(notifyActive);
  }
  return store;
}

/**
 * Is anything running IN THE ACTIVE ORGANIZATION?
 *
 * Scoped deliberately: this drives the migrations list's 3-second poll, and a
 * run in an org the user is not looking at is not a reason to poll the org they
 * are looking at.
 */
function anyRunActive(): boolean {
  const prefix = `${getActiveOrgId() ?? '__no_org__'}::`;
  for (const [key, store] of runStores) {
    if (!key.startsWith(prefix)) continue;
    if (store.getState().status === "running") return true;
  }
  return false;
}

// Reactive "is any skill actively running?" — replaces the singleton's global
// status read that drove the migrations-list 3s poll.
export function useAnyRunActive(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      activeListeners.add(onChange);
      return () => activeListeners.delete(onChange);
    },
    anyRunActive,
    anyRunActive,
  );
}

// Subscribe a component to ONE skill's run store. Every consumer threads its
// skillId, so concurrent migrations never share a slot.
export function useRunStore(skillId: string): RunState;
export function useRunStore<U>(skillId: string, selector: (s: RunState) => U): U;
export function useRunStore<U>(skillId: string, selector?: (s: RunState) => U) {
  const store = getRunStore(skillId);
  return useStore(store, selector ?? ((s) => s as unknown as U));
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// The registry is already keyed by org, so a switch needs no teardown: the new
// org's `getRunStore` calls resolve to different instances and its list is
// clean. All this registration does is wake the `useAnyRunActive` subscribers so
// the aggregate re-reads under the new key prefix — without it a component would
// keep displaying the previous org's answer until some unrelated run event
// happened to fire.
registerOrgScopedState({
  name: "studioRunStore",
  onOrgChange: () => {
    notifyActive();
  },
});
